import { useMemo, useCallback } from 'react'
import ReactSelect from 'react-select'
import CreatableSelect from 'react-select/creatable'
import cn from 'classnames'

import withAddons from '../adapters/with-addons'
import { MultiSelectContextProvider } from './multi-select-context'
import { Control, MultiValue, SelectCheckboxDropdown } from './custom-components'
import SelectView from './select-view'

import './select.scss'
import s from './select-view.module.scss'
import CheckboxTreeParentOption from './custom-components/checkbox-tree-parent/checkbox-tree-parent'

export type SelectOption = {
  label: string | number
  value: string | number
  options? : Array<SelectOption>
}

type SelectProps = {
  isInvalid: boolean
  value: string | number | Array<string> | Array<number>
  onChange: Function
  isMulti: boolean
  isMultiCheckboxTree: boolean
  options: Array<SelectOption>
  name: string
  isClearable: boolean
  isViewMode: boolean
  disabled: boolean | undefined
  canCreate: boolean
  onCreateOption?: (input: string) => any
  innerRef?: any
  showSearchIcon?: boolean
}

const ALL = 'All'

const isMultiValueIncludeAll = (value: any) => Boolean(value.find((v: any) => v.value === ALL))

const parseOptionValue = (value: any, isMulti: boolean) => {
  if (isMulti) {
    if (Array.isArray(value)) {
      const isAllSelected = isMultiValueIncludeAll(value)
      if (isAllSelected) {
        return ALL
      } else {
        return (value || []).map(v => v.value)
      }
    } else {
      return [value.value]
    }
  } else {
    return value.value
  }
}

const normalizeOptionValue = (value: any, options: Array<SelectOption>) => {
  // Flatten options if they are grouped (MultiCheckboxTree)
  const flatOptions = options.flatMap(opt => Array.isArray(opt.options) ? opt.options : [opt])
  if (Array.isArray(value)) {
    return value.map(v => flatOptions.find(o => o.value === v)).filter(Boolean)
  }
  return flatOptions.find(o => o.value === value) ? [flatOptions.find(o => o.value === value)] : []
}

const initialContextState = {}

// Custom Control with optional icon
const CustomControlWithSearchIcon = (props: any) => (
  <Control {...props}>
    <i className={cn('bi bi-search', s['search-icon'])} />
    {props.children}
  </Control>
)

const Select: React.FC<SelectProps> = props => {
  const {
    isInvalid,
    value,
    onChange,
    isMulti,
    options = [],
    disabled,
    isViewMode,
    isClearable,
    innerRef,
    showSearchIcon = false,
    isMultiCheckboxTree = false,
    ...selectProps
  } = props
  const _isMulti = isMulti && value !== ALL
  const _isMultiCheckboxTree = isMultiCheckboxTree && value !== ALL

  const customStyles = useMemo(
    () => {
      const baseStyles = {
        menuPortal: (base: any) => ({
          ...base,
          zIndex: 9999,
        }),
        valueContainer: (provided: any, state: any) => ({
          ...provided,
          justifyContent: 'flex-start',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          flexWrap: 'nowrap',
        }),
      }
      if (_isMultiCheckboxTree) {
        return {
          ...baseStyles,
          option: (provided: any, state: any) => ({
            ...provided,
            paddingTop: '0',
            paddingBottom: '0',
            backgroundColor: state.isSelected ? '#B2D4FF' : state.isFocused ? '#fafbfc' : 'transparent',
            '&:active': {
              backgroundColor: '#f0f4ff',
            },
            color: 'inherit',
            cursor: 'pointer',
          }),
          groupHeading: (provided: any, state: any) => ({
            ...provided,
            backgroundColor: state.isSelected ? '#B2D4FF' : state.isFocused ? '#fafbfc' : 'transparent',
            '&:active': {
              backgroundColor: '#f0f4ff',
            },
            '&:hover': {
              backgroundColor: '#fafbfc',
            },
            opacity: 1,
            fontSize: 'inherit',
            textTransform: 'none',
            color: '#3f4254',
            cursor: 'pointer',
          }),
        }
      } else {
        return baseStyles
      }
    },
    [_isMultiCheckboxTree],
  )

  const multiValueComponents = {
    Control: Control,
    MultiValue: MultiValue,
    Option: SelectCheckboxDropdown,
  }

  const multiValueWithCheckboxTreeComponents = {
    Control: CustomControlWithSearchIcon,
    MultiValue: MultiValue,
    Option: SelectCheckboxDropdown,
    GroupHeading: CheckboxTreeParentOption,
  }

  const customComponents = {
    IndicatorSeparator: () => null,
    Control: showSearchIcon ? CustomControlWithSearchIcon : Control,
  }

  if (_isMulti) {
    Object.keys(multiValueComponents).forEach(k => {
      // @ts-ignore
      customComponents[k] = multiValueComponents[k]
    })
  }

  if (_isMultiCheckboxTree) {
    Object.keys(multiValueWithCheckboxTreeComponents).forEach(k => {
      // @ts-ignore
      customComponents[k] = multiValueWithCheckboxTreeComponents[k]
    })
  }

  const _onChange = useCallback(
    (value: any) => {
      if (value === null) {
        onChange && onChange(undefined)
        return
      }
      const formattedValue = parseOptionValue(value, (isMulti || isMultiCheckboxTree))
      onChange && onChange(formattedValue)
    },
    [onChange, isMulti, isMultiCheckboxTree],
  )

  const _value = useMemo(() => {
    return normalizeOptionValue(value, options)
  }, [value, options])

  const selectClassNames = cn('react-select zindex-5', disabled && 'opacity-50')
  const prefixClassNames = cn({ 'react-select-danger': isInvalid }, 'react-select')

  const filterParentAndOptionLabels = (option: any, inputValue: string) => {
    const formattedInputValue = inputValue ? inputValue.toLowerCase() : ''

    const findMatches = (stack: any) => {
      return typeof stack === 'string' && stack.toLowerCase().includes(formattedInputValue)
    }

    // Check if the option label, value, or parent label matches
    const optionMatches = findMatches(option.label) || findMatches(option.value)
    const parentMatches = option.data.parentLabel && findMatches(option.data.parentLabel)

    return optionMatches || parentMatches
  }

  return isViewMode ? (
    <SelectView value={value} options={options} />
  ) : props.canCreate && props.onCreateOption ? (
    <MultiSelectContextProvider value={initialContextState}>
      <CreatableSelect
        ref={innerRef}
        className={selectClassNames}
        classNamePrefix={prefixClassNames}
        closeMenuOnSelect={!(_isMulti || _isMultiCheckboxTree) ? true : false}
        filterOption={_isMultiCheckboxTree ? filterParentAndOptionLabels : undefined}
        components={customComponents}
        hideSelectedOptions={false}
        options={options}
        onChange={_onChange}
        value={_value}
        isMulti={_isMulti || _isMultiCheckboxTree}
        isDisabled={disabled}
        menuPortalTarget={document.body}
        styles={customStyles}
        isClearable={isClearable}
        createOptionPosition="first"
        menuPlacement="auto"
        formatCreateLabel={(inputValue: string) => `"${inputValue}"`}
        {...selectProps}
      />
    </MultiSelectContextProvider>
  ) : (
    <MultiSelectContextProvider value={initialContextState}>
      <ReactSelect
        ref={innerRef}
        className={selectClassNames}
        classNamePrefix={prefixClassNames}
        closeMenuOnSelect={!(_isMulti || _isMultiCheckboxTree) ? true : false}
        filterOption={_isMultiCheckboxTree ? filterParentAndOptionLabels : undefined}
        components={customComponents}
        hideSelectedOptions={false}
        options={options}
        onChange={_onChange}
        value={_value}
        isMulti={_isMulti || _isMultiCheckboxTree}
        isDisabled={disabled}
        menuPortalTarget={document.body}
        styles={customStyles}
        isClearable={isClearable}
        menuPlacement="auto"
        {...selectProps}
      />
    </MultiSelectContextProvider>
  )
}

export default withAddons(Select)
